import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  ArrowRight,
  CalendarCheck,
  ChevronDown,
  Clock,
  FileText,
  Wallet,
  Phone,
  PhoneMissed,
  Send,
} from 'lucide-react';
import { TIME_RANGES, type DashboardFinances, type DashboardSummary, type Reminder, type TimeRange } from '@worki/shared';
import { api } from '../lib/api.js';
import { useAuthenticated } from '../lib/session.js';
import { currentLocale } from '../i18n.js';
import { money } from '../lib/format.js';
import { LoadingScreen } from '../components/ui.js';
import { AnchoredMenu } from '../components/AnchoredMenu.js';
import { ReminderDialog } from '../components/ReminderDialog.js';
import { DashboardRevenue } from '../components/DashboardRevenue.js';
import { cn } from '../lib/cn.js';

/**
 * What to say at the top of the screen, given the hour it was opened at.
 *
 * Read downwards: the first band whose hour has been reached wins, and anything
 * before five in the morning falls off the end into the night it belongs to.
 * The bands are Israeli rather than clock-symmetrical — "צהריים" is the middle
 * of the day and not the single hour of noon, and an evening that starts at
 * four is what someone finishing a job would call it.
 */
const GREETINGS: Array<[fromHour: number, key: string]> = [
  [21, 'night'],
  [16, 'evening'],
  [12, 'noon'],
  [5, 'morning'],
];

const greetingKey = (hour: number): string =>
  GREETINGS.find(([from]) => hour >= from)?.[1] ?? 'night';

/**
 * The first name, out of whatever the account was opened with.
 *
 * "שלום, ישראל ישראלי" is a form letter; the person reading their own
 * dashboard is greeted the way anyone would greet them. Falls back to the whole
 * string, so a one-word name and a name this cannot split are both left alone.
 */
const firstName = (name: string): string => name.trim().split(/\s+/)[0] || name;

/** DASH-003 — the selected range lives in sessionStorage, so it survives navigation. */
const RANGE_KEY = 'worki.dashboard.range';

function storedRange(): TimeRange {
  const value = sessionStorage.getItem(RANGE_KEY);
  return TIME_RANGES.includes(value as TimeRange) ? (value as TimeRange) : 'week';
}

/** The arrow that means "go on to the full list" flips with the writing direction. */
function ForwardArrow() {
  const isRtl = document.documentElement.dir === 'rtl';
  const Arrow = isRtl ? ArrowLeft : ArrowRight;
  return <Arrow className="h-3.5 w-3.5" />;
}

export function DashboardPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const session = useAuthenticated();
  const locale = currentLocale();

  const [range, setRange] = useState<TimeRange>(storedRange);
  const [showRange, setShowRange] = useState(false);
  const rangeBtnRef = useRef<HTMLButtonElement>(null);
  const [selectedMonth, setSelectedMonth] = useState('');
  const now = new Date();
  const latestMonth = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}`;
  const selectFinancialMonth = (month: string) => {
    if (month < '1900-01' || month > latestMonth) return;
    setSelectedMonth(month);
    setRange('month');
  };
  const periodParams = new URLSearchParams({ range });
  if (selectedMonth) { const [year, month] = selectedMonth.split('-'); periodParams.set('year', year!); periodParams.set('month', String(Number(month))); }
  const periodQuery = periodParams.toString();
  const moveFinancialMonth = (delta: number) => {
    const anchor = selectedMonth ? new Date(`${selectedMonth}-15T12:00:00`) : new Date();
    anchor.setDate(1); anchor.setMonth(anchor.getMonth() + delta);
    selectFinancialMonth(`${anchor.getFullYear()}-${String(anchor.getMonth() + 1).padStart(2, '0')}`);
  };
  const [reminderOpen, setReminderOpen] = useState(false);
  const [editing, setEditing] = useState<Reminder | null>(null);

  const summary = useQuery({
    queryKey: selectedMonth ? ['dashboard', range, selectedMonth] : ['dashboard', range],
    queryFn: () => api.get<DashboardSummary>(`/dashboard/summary?${periodQuery}`),
  });
  const finances = useQuery({
    queryKey: ['dashboard', 'finances', periodQuery],
    queryFn: () => api.get<DashboardFinances>(`/dashboard/finances?${periodQuery}`),
  });

  const changeRange = (next: TimeRange) => {
    sessionStorage.setItem(RANGE_KEY, next);
    setRange(next);
    setSelectedMonth('');
  };

  if (summary.isLoading || !summary.data) return <LoadingScreen />;

  const { kpis } = summary.data;

  const contentDirection = document.documentElement.dir === 'rtl' ? 'rtl' : 'ltr';

  /*
   * A month with nothing before it has no percentage to show — dividing by
   * zero would print "Infinity% vs last month", and "+100%" would be a claim
   * about a month that did not happen. It says how many instead.
   */
  const { meetingsThisMonth, meetingsLastMonth } = summary.data;
  const monthOverMonth =
    meetingsLastMonth === 0
      ? t('dashboard.meetingsThisMonth', { count: meetingsThisMonth })
      : t('dashboard.vsLastMonth', {
          percent: `${meetingsThisMonth >= meetingsLastMonth ? '+' : ''}${Math.round(
            ((meetingsThisMonth - meetingsLastMonth) / meetingsLastMonth) * 100,
          )}`,
        });
  const todayLabel = new Intl.DateTimeFormat(locale === 'he' ? 'he-IL' : 'en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(new Date());

  const quickActions = [
    { key: 'newReminder', icon: CalendarCheck, label: t('dashboard.newReminderMeeting'), run: () => { setEditing(null); setReminderOpen(true); } },
    { key: 'newQuote', icon: Send, label: t('dashboard.newQuote'), run: () => navigate('/quotes/new') },
  ];

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <header dir={contentDirection} className="animate-fade-up space-y-1">
        <h1 dir={contentDirection} className="text-xl font-bold text-start sm:text-2xl">
          {t(`dashboard.greeting.${greetingKey(new Date().getHours())}`, { name: firstName(session.user.name) })}
        </h1>
        <div dir={contentDirection} className="flex items-center gap-2 flex-wrap">
        <div className="relative">
          <button
            type="button"
            ref={rangeBtnRef}
            onClick={() => {
              setShowRange((v) => !v);
            }}
            aria-expanded={showRange}
            aria-label={`${todayLabel} · ${selectedMonth ? new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(new Date(`${selectedMonth}-15T12:00:00`)) : t(`dashboard.range.${range}`)}`}
            className="min-h-11 rounded-lg hover:bg-muted transition-colors flex items-center gap-2 text-start"
          >
            <time className="text-sm font-medium" dateTime={new Date().toLocaleDateString('en-CA')}>{todayLabel}</time>
            <ChevronDown
              className={cn('h-4 w-4 text-muted-foreground transition-transform', showRange && 'rotate-180')}
            />
          </button>
          <AnchoredMenu open={showRange} onClose={() => setShowRange(false)} anchorRef={rangeBtnRef} width={160}>
            {TIME_RANGES.map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => {
                  changeRange(option);
                  setShowRange(false);
                }}
                className={cn(
                  'w-full text-start px-4 py-2.5 text-sm hover:bg-muted/60 transition-colors',
                  range === option && 'text-primary font-semibold bg-primary/5',
                )}
              >
                {t(`dashboard.range.${option}`)}
              </button>
            ))}
            <label className="block px-3 py-2 border-t border-border text-xs">{t('completion.calendarMonth')}
              <input type="month" aria-label={t('completion.calendarMonth')} min="1900-01" max={latestMonth} className="mt-1 min-h-11 w-full rounded-lg border border-border bg-background px-2 text-sm" value={selectedMonth} onChange={event => { if (event.target.value) selectFinancialMonth(event.target.value); else setSelectedMonth(''); }} />
            </label>
          </AnchoredMenu>
        </div>

        </div>
      </header>

      {/* Stats (DASH-004/005) — each card opens the list it counts. */}
      <section className="animate-fade-up stagger-1" aria-labelledby="dashboard-stats-title">
        <div className="flex items-center justify-between mb-3">
          <h2 id="dashboard-stats-title" className="text-base font-bold">{t('dashboard.stats')}</h2>
          <button
            type="button"
            onClick={() => navigate(`/reports?range=${range}`)}
            className="text-sm text-primary hover:underline flex items-center gap-1"
          >
            {t('common.viewAll')} <ForwardArrow />
          </button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {/* The list at the same range, and not at a tab: "sent" was one, and
              what it held was every quote but the drafts — which is what this
              card counts, so the tab it opened narrowed nothing. */}
          <button
            type="button"
            onClick={() => navigate(`/quotes?${periodQuery}`)}
            className="rounded-2xl p-4 text-start hover:shadow-md transition-all border border-border/60 bg-card"
          >
            <div className="flex items-center gap-2.5 mb-3">
              <div className="p-2 rounded-xl bg-primary/10">
                <FileText className="h-5 w-5 text-primary" />
              </div>
              <p className="text-sm font-semibold">{t('quotes.title')}</p>
            </div>
            <p className="text-3xl font-bold ltr-nums">{kpis.quotesSent}</p>
            <p className="text-[11px] text-muted-foreground">{t('dashboard.kpi.sentShort')}</p>
            <div className="mt-3 pt-3 border-t border-border/60 flex flex-wrap items-center justify-between text-xs gap-2">
              <span>{t('completion.viewed', { count: kpis.quotesViewed ?? 0 })}</span>
              <span className="text-success">{t('completion.approved', { count: kpis.quotesApproved })}</span>
              <span className="flex items-center gap-1 text-warning font-semibold">
                <Clock className="h-3.5 w-3.5" />
                {t('dashboard.kpi.pendingShort', { count: kpis.quotesPending })}
              </span>
              <span className="flex items-center gap-1 text-destructive font-semibold">
                {t('dashboard.kpi.rejectedShort', { count: kpis.quotesRejected })}
              </span>
            </div>
          </button>

          <button
            type="button"
            onClick={() => navigate(`/calls?direction=inbound&${periodQuery}`)}
            className="rounded-2xl p-4 text-start hover:shadow-md transition-all border border-border/60 bg-card"
          >
            <div className="flex items-center gap-2.5 mb-3">
              <div className="p-2 rounded-xl bg-success/10">
                <Phone className="h-5 w-5 text-success" />
              </div>
              <p className="text-sm font-semibold">{t('dashboard.kpi.incomingCalls')}</p>
            </div>
            <p className="text-3xl font-bold ltr-nums">{kpis.incomingCalls}</p>
            <p className="text-[11px] text-muted-foreground">{t('dashboard.kpi.callsTotal')}</p>
            <div className="mt-3 pt-3 border-t border-border/60 flex items-center justify-between text-xs">
              <span className="flex items-center gap-1 text-destructive font-semibold">
                <PhoneMissed className="h-3.5 w-3.5" />
                {t('dashboard.kpi.missedShort', { count: kpis.missedCalls })}
              </span>
            </div>
          </button>
          <button type="button" onClick={() => navigate('/calendar')} className="rounded-2xl p-4 text-start hover:shadow-md transition-all border border-border/60 bg-card">
            <div className="flex items-center gap-2.5 mb-3">
              <div className="p-2 rounded-xl bg-info/10"><CalendarCheck className="h-5 w-5 text-info" /></div>
              <p className="text-sm font-semibold">{t('dashboard.meetings')}</p>
            </div>
            <p className="text-3xl font-bold ltr-nums">{summary.data.meetingsInPeriod ?? meetingsThisMonth}</p>
            <p className="text-[11px] text-muted-foreground">{t('completion.periodMeetings')}</p>
            {meetingsLastMonth > 0 && <p className="mt-3 pt-3 border-t border-border/60 text-xs text-muted-foreground">{monthOverMonth}</p>}
          </button>
          <button type="button" onClick={() => navigate(`/invoices?${periodQuery}`)} className="rounded-2xl p-4 text-start hover:shadow-md transition-all border border-border/60 bg-card">
            <div className="flex items-center gap-2.5 mb-3">
              <div className="p-2 rounded-xl bg-warning/10"><Wallet className="h-5 w-5 text-warning" /></div>
              <p className="text-sm font-semibold">{t('dashboard.finances.archivedInvoices')}</p>
            </div>
            {finances.data ? <>
              <div className="flex items-center justify-between gap-2 text-sm"><span>{t('dashboard.finances.income')}</span><strong className="ltr-nums">{money(finances.data.incomeTotal, finances.data.currency, locale)}</strong></div>
              <div className="flex items-center justify-between gap-2 text-sm mt-2"><span>{t('dashboard.finances.expenses')}</span><strong className="ltr-nums">{money(finances.data.expenseTotal, finances.data.currency, locale)}</strong></div>
              <div className="flex items-center justify-between gap-2 text-sm mt-2 border-t border-border pt-2"><span>{t('completion.profit')}</span><strong className="ltr-nums">{money(finances.data.profitTotal ?? finances.data.incomeTotal - finances.data.expenseTotal, finances.data.currency, locale)}</strong></div>
              <p className="text-[11px] text-muted-foreground mt-3">{t('completion.selectedPeriod')} · {finances.data.currency}</p>
            </> : <p className="text-xs text-muted-foreground">{t(finances.isError ? 'dashboard.finances.unavailable' : 'common.loading')}</p>}
          </button>
        </div>
      </section>

      {finances.data ? <DashboardRevenue finances={finances.data} selectedMonth={selectedMonth} onMonthChange={moveFinancialMonth} onMonthSelect={selectFinancialMonth} onYearChange={year=>selectFinancialMonth(`${year}-${year===now.getFullYear()?String(now.getMonth()+1).padStart(2,'0'):'12'}`)} /> : (
        <p className="text-sm text-muted-foreground" role="status">{t(finances.isError ? 'dashboard.finances.unavailable' : 'common.loading')}</p>
      )}

      {/* Quick actions (DASH-010). */}
      <section className="animate-fade-up stagger-3">
        <h2 className="text-base font-bold mb-3">{t('dashboard.quickActions')}</h2>
        <div className="grid grid-cols-2 gap-3">
          {quickActions.map((action) => {
            const Icon = action.icon;
            return (
              <button
                key={action.key}
                type="button"
                onClick={action.run}
                className="rounded-xl border border-border/60 bg-card p-4 flex flex-col items-center gap-3 cursor-pointer hover:shadow-md hover:border-primary/20 transition-all active:scale-[0.97]"
              >
                <div className="p-3 rounded-xl bg-primary/10">
                  <Icon className="h-6 w-6 text-primary" />
                </div>
                <span className="text-sm font-medium text-center">{action.label}</span>
              </button>
            );
          })}
        </div>
      </section>

      <ReminderDialog
        open={reminderOpen}
        reminder={editing}
        onClose={() => {
          setReminderOpen(false);
          setEditing(null);
        }}
      />
    </div>
  );
}
